import React, { useMemo, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { Search, RefreshCw, ExternalLink, Slash, RotateCcw, Printer, Mail, MessageCircle } from "react-feather";
import AmcRegisterPrint from "./AmcRegisterPrint";
import StatusBadge from "../../../components/common/StatusBadge";
import DateRangePicker, { useDatePresetRange } from "../../../components/common/DateRangePicker";

const STATUS_CLASSES = {
  ACTIVE: "badge-linesuccess",
  EXPIRED: "badge-linedanger",
  COMPLETED: "badge-lineinfo",
  CANCELLED: "badge-linedark",
};
const STATUS_LABELS = {
  ACTIVE: "Active",
  EXPIRED: "Expired",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};

// Filtered on effectiveStatus, which the server computes from the dates - the stored
// Status column only ever says ACTIVE/COMPLETED/CANCELLED, so "Expired" and "Expiring"
// are not values you can ask the API for. They are the two an operator actually cares
// about, so they are resolved here from what the row already carries.
const FILTERS = [
  { key: "ALL", label: "All" },
  { key: "ACTIVE", label: "Active" },
  { key: "EXPIRING", label: "Expiring soon" },
  { key: "EXPIRED", label: "Expired" },
  { key: "CLOSED", label: "Closed" },
];

const EXPIRING_WINDOW_DAYS = 60;

// Which contract date the date range applies to (2026-09-11). "Running" is an
// overlap test - a contract counts if any part of its period falls in the range,
// the "which AMCs did we have in March" question. Off by default so the page
// still opens on the whole register.
const DATE_BASES = [
  { key: "ANY", label: "Any date" },
  { key: "START", label: "Starting between" },
  { key: "END", label: "Ending between" },
  { key: "RUNNING", label: "Running during" },
];

// Dates arrive as ISO strings; the first 10 characters (YYYY-MM-DD) compare
// correctly as plain strings, the same form the range picker gives.
const matchesDates = (row, basis, from, to) => {
  if (basis === "ANY" || !from || !to) return true;
  const start = row.startDate ? String(row.startDate).substring(0, 10) : null;
  const end = row.endDate ? String(row.endDate).substring(0, 10) : null;
  switch (basis) {
    case "START":
      return Boolean(start) && start >= from && start <= to;
    case "END":
      return Boolean(end) && end >= from && end <= to;
    case "RUNNING":
      return Boolean(start) && Boolean(end) && start <= to && end >= from;
    default:
      return true;
  }
};

const fmt = (v) => (v ? String(v).substring(0, 10) : "-");
// Blank rather than 0 when no amount was recorded - a contract with no figure
// on it is not a free contract, and showing 0.00 would quietly imply it is.
const money = (v) =>
  v == null ? "-" : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const matchesFilter = (row, filter) => {
  switch (filter) {
    case "ACTIVE":
      return row.effectiveStatus === "ACTIVE";
    case "EXPIRING":
      return row.effectiveStatus === "ACTIVE" && row.daysToExpiry >= 0 && row.daysToExpiry <= EXPIRING_WINDOW_DAYS;
    case "EXPIRED":
      return row.effectiveStatus === "EXPIRED";
    case "CLOSED":
      return row.effectiveStatus === "COMPLETED" || row.effectiveStatus === "CANCELLED";
    default:
      return true;
  }
};

/**
 * Every AMC on record, which is what this screen now opens on.
 *
 * It used to open on an empty "Choose a customer to start" panel: you could only see an AMC
 * if you already knew whose it was, so there was no way to answer "what have we got?" at
 * all. The customer picker above is now a filter over this list rather than a gate in front
 * of it, and picking a row is what opens the contract, its equipment and its schedule below.
 *
 * Filtering and search are client-side deliberately. The list is branch-scoped server-side
 * and an AMC register is a few hundred rows at most, so filtering in the browser is instant
 * where a round trip per keystroke would not be.
 */
const AmcContractList = ({ rows, isLoading, selectedGuid, onOpen, onRenew, onSetStatus, onSendReminder, canManage, customerFiltered }) => {
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [dateBasis, setDateBasis] = useState("ANY");
  const { preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo } = useDatePresetRange("THIS_YEAR");

  // The date range narrows the register first; the status buttons then count
  // and filter within it, so their numbers always describe the chosen period.
  const inDateRange = useMemo(
    () => rows.filter((r) => matchesDates(r, dateBasis, dateFrom, dateTo)),
    [rows, dateBasis, dateFrom, dateTo]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return inDateRange.filter(
      (r) =>
        matchesFilter(r, filter) &&
        (!term ||
          r.contractNo?.toLowerCase().includes(term) ||
          r.customerName?.toLowerCase().includes(term) ||
          r.branchCode?.toLowerCase().includes(term) ||
          r.invoiceNumber?.toLowerCase().includes(term))
    );
  }, [inDateRange, filter, search]);

  const counts = useMemo(
    () => Object.fromEntries(FILTERS.map((f) => [f.key, inDateRange.filter((r) => matchesFilter(r, f.key)).length])),
    [inDateRange]
  );

  // Tick boxes (2026-09-17, customer request): Print takes only the ticked rows,
  // or everything on screen when nothing is ticked. Ticks survive filter changes,
  // but only rows still on screen are printed or counted.
  const [picked, setPicked] = useState(() => new Set());
  const pickedRows = useMemo(() => filtered.filter((r) => picked.has(r.recordGuid)), [filtered, picked]);
  const allVisiblePicked = filtered.length > 0 && pickedRows.length === filtered.length;
  const togglePick = (guid) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(guid)) next.delete(guid);
      else next.add(guid);
      return next;
    });
  const toggleAllVisible = () =>
    setPicked((prev) => {
      const next = new Set(prev);
      filtered.forEach((r) => (allVisiblePicked ? next.delete(r.recordGuid) : next.add(r.recordGuid)));
      return next;
    });
  const printRows = pickedRows.length > 0 ? pickedRows : filtered;

  const dateLabel =
    dateBasis === "ANY" ? null : `${DATE_BASES.find((b) => b.key === dateBasis)?.label} ${fmt(dateFrom)} and ${fmt(dateTo)}`;

  // Same about:blank popup + renderToStaticMarkup approach as the petty cash
  // voucher and the stock documents - one template serves both the paper and
  // any future on-screen preview.
  const handlePrint = () => {
    const bodyHtml = ReactDOMServer.renderToStaticMarkup(
      <AmcRegisterPrint
        rows={printRows}
        // A hand-picked set is not "the filter" any more - say so instead of
        // repeating filter, date and search labels that no longer describe it.
        filterLabel={pickedRows.length > 0 ? "Selected contracts" : FILTERS.find((f) => f.key === filter)?.label}
        customerName={customerFiltered ? printRows[0]?.customerName : null}
        dateLabel={pickedRows.length > 0 ? null : dateLabel}
        searchTerm={pickedRows.length > 0 ? null : search.trim() || null}
      />
    );
    const win = window.open("", "_blank", "width=1100,height=900");
    if (!win) return;
    win.document.write(
      `<html><head><title>AMC Register</title>` +
        `<style>@page { size: A4 landscape; margin: 10mm; } html,body { margin:0; padding:0; }</style>` +
        `</head><body onload="window.print();window.close();">${bodyHtml}</body></html>`
    );
    win.document.close();
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <h5 className="mb-0">AMC Records</h5>
            <small className="text-muted">
              {customerFiltered
                ? "Filtered to the selected customer - clear the customer above to see them all."
                : "Every AMC on record. Pick one to open its equipment and service schedule."}
            </small>
          </div>

          <div className="d-flex align-items-center flex-wrap gap-2">
            <div className="btn-group btn-group-sm" role="group">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={`btn ${filter === f.key ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                  {counts[f.key] > 0 && <span className="ms-1">({counts[f.key]})</span>}
                </button>
              ))}
            </div>

            <div className="input-group input-group-sm" style={{ width: 220 }}>
              <span className="input-group-text">
                <Search size={14} />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Contract, customer, branch, invoice"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Prints the ticked rows, or - with nothing ticked - exactly what is
                on screen (same filter and search). */}
            <button
              type="button"
              className={`btn btn-sm d-inline-flex align-items-center gap-1 ${pickedRows.length ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={handlePrint}
              disabled={filtered.length === 0}
              title={
                pickedRows.length
                  ? "Print the ticked contracts with amounts and a running total"
                  : "Print this list with amounts and a running total (tick rows to print only those)"
              }
            >
              <Printer size={14} /> {pickedRows.length ? `Print selected (${pickedRows.length})` : "Print"}
            </button>
            {pickedRows.length > 0 && (
              <button type="button" className="btn btn-sm btn-link px-1" onClick={() => setPicked(new Set())}>
                Clear selection
              </button>
            )}
          </div>
        </div>

        {/* Date filter - its own row; the preset chips only appear once a date
            basis is chosen, so the default screen stays as uncluttered as before. */}
        <div className="d-flex align-items-start flex-wrap gap-2 mb-3">
          <select
            className="form-select form-select-sm"
            style={{ width: 180 }}
            value={dateBasis}
            onChange={(e) => setDateBasis(e.target.value)}
            aria-label="Filter by contract date"
          >
            {DATE_BASES.map((b) => (
              <option key={b.key} value={b.key}>{b.label}</option>
            ))}
          </select>
          {dateBasis !== "ANY" && (
            <DateRangePicker
              preset={preset} setPreset={setPreset} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo}
            />
          )}
        </div>

        {isLoading && <p className="text-muted mb-0">Loading…</p>}

        {!isLoading && rows.length === 0 && (
          <p className="text-muted mb-0">
            No AMC has been recorded yet. Pick a customer above and create the first one.
          </p>
        )}

        {!isLoading && rows.length > 0 && filtered.length === 0 && (
          <p className="text-muted mb-0">
            Nothing matches that filter{dateBasis !== "ANY" ? " in the chosen dates" : ""}.
          </p>
        )}

        {filtered.length > 0 && (
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ width: 28 }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={allVisiblePicked}
                      onChange={toggleAllVisible}
                      aria-label="Select all shown contracts for printing"
                      title="Select all shown"
                    />
                  </th>
                  {/* Customer first, contract number second (2026-09-12) - the
                      customer is what people scan this register for. */}
                  <th>Customer</th>
                  <th>Contract</th>
                  <th>Period</th>
                  <th>Every</th>
                  <th>Visits</th>
                  <th>Status</th>
                  <th className="text-end">Amount</th>
                  {/* Batch154 - the register is where "who still owes us" gets
                      scanned, so invoice/paid/balance belong here and not only
                      on the editor below. */}
                  <th>Invoice</th>
                  <th className="text-end">Paid</th>
                  <th className="text-end">Balance</th>
                  <th className="text-end">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const isSelected = r.recordGuid === selectedGuid;
                  return (
                    <tr key={r.recordGuid} className={isSelected ? "table-active" : undefined}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={picked.has(r.recordGuid)}
                          onChange={() => togglePick(r.recordGuid)}
                          aria-label={`Select ${r.contractNo} for printing`}
                        />
                      </td>
                      <td>
                        <span className="fw-bold">{r.customerName || `Customer ${r.custId}`}</span>
                        {r.locationLabel && <small className="d-block text-muted">{r.locationLabel}</small>}
                      </td>
                      <td>
                        <span className="text-muted">{r.contractNo}</span>
                        <small className="d-block text-muted">{r.branchCode}</small>
                      </td>
                      <td>
                        {fmt(r.startDate)} to {fmt(r.endDate)}
                        <small className="d-block text-muted">
                          {r.daysToExpiry >= 0
                            ? `${r.daysToExpiry} day(s) left`
                            : `expired ${Math.abs(r.daysToExpiry)} day(s) ago`}
                        </small>
                      </td>
                      <td>{r.frequency?.replace(/_/g, " ").toLowerCase()}</td>
                      <td>
                        {r.visitsDone}/{r.visitCount}
                        {r.visitsDue > 0 && <small className="d-block text-danger">{r.visitsDue} due</small>}
                      </td>
                      <td>
                        <StatusBadge status={r.effectiveStatus} classMap={STATUS_CLASSES} labelMap={STATUS_LABELS} />
                      </td>
                      <td className="text-end">
                        {money(r.amcAmount)}
                        {/* Shows a reminder actually went out, so nobody chases the
                            same customer twice in a week. Only SENT attempts set this
                            server-side - a failed one must not read as "chased". */}
                        {r.lastReminderSentDate && (
                          <small className="d-block text-muted" style={{ fontSize: 10.5 }}>
                            reminded {fmt(r.lastReminderSentDate)}
                          </small>
                        )}
                      </td>
                      <td>
                        {r.invoiceNumber ? (
                          <span className="fw-semibold">{r.invoiceNumber}</span>
                        ) : (
                          <span className="badge badge-linedark">Not invoiced</span>
                        )}
                      </td>
                      <td className="text-end">{r.paidAmount == null ? <span className="text-muted">—</span> : money(r.paidAmount)}</td>
                      <td className="text-end">
                        {/* Derived here exactly as it is in the editor - never a
                            stored column, so the two can't disagree. */}
                        {r.amcAmount == null ? (
                          <span className="text-muted">—</span>
                        ) : (
                          (() => {
                            const balance = Number(r.amcAmount) - (Number(r.paidAmount) || 0);
                            if (balance === 0) return <span className="text-success fw-semibold">settled</span>;
                            if (balance < 0)
                              return <span style={{ color: "#B4451F" }} className="fw-semibold">+{money(Math.abs(balance))}</span>;
                            return <span className="fw-semibold">{money(balance)}</span>;
                          })()
                        )}
                      </td>
                      <td className="text-end">
                        <div className="d-flex gap-2 justify-content-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                            onClick={() => onOpen(r)}
                          >
                            {/* Opening IS the edit path - it loads the contract into the
                                editor below. Labelled so for a manager, because "Open"
                                alone read as view-only and people could not find how to
                                edit a contract. */}
                            <ExternalLink size={13} /> {canManage ? "Open / Edit" : "Open"}
                          </button>
                          {/* Renewing prefills a fresh contract carrying on from this one's
                              end date and links the two, which is what takes this contract
                              off the pending-renewal list on the dashboard. */}
                          {canManage && r.effectiveStatus !== "CANCELLED" && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1"
                              onClick={() => onRenew(r)}
                            >
                              <RefreshCw size={13} /> Renew
                            </button>
                          )}
                          {/* Cancel stops the contract being counted in the service-due and
                              renewals-due lists while leaving it visible here under a
                              CANCELLED badge. COMPLETED is an end state the server refuses
                              to flip, so it is not offered. */}
                          {/* Renewal reminders - one icon per channel, sent only
                              when clicked. There is no automatic send anywhere in
                              this feature. Offered on contracts that are still
                              running or already expired (an expired one is the
                              most worth chasing); a cancelled contract is not. */}
                          {onSendReminder && r.effectiveStatus !== "CANCELLED" && (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center"
                                title={
                                  r.lastReminderSentDate
                                    ? `Email renewal reminder · last sent ${fmt(r.lastReminderSentDate)}`
                                    : "Email renewal reminder"
                                }
                                onClick={() => onSendReminder(r, ["EMAIL"])}
                              >
                                <Mail size={13} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-success d-inline-flex align-items-center"
                                title={
                                  r.lastReminderSentDate
                                    ? `WhatsApp renewal reminder · last sent ${fmt(r.lastReminderSentDate)}`
                                    : "WhatsApp renewal reminder"
                                }
                                onClick={() => onSendReminder(r, ["WHATSAPP"])}
                              >
                                <MessageCircle size={13} />
                              </button>
                            </>
                          )}
                          {canManage && onSetStatus && r.effectiveStatus === "ACTIVE" && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1"
                              onClick={() => onSetStatus(r, "CANCELLED")}
                            >
                              <Slash size={13} /> Cancel
                            </button>
                          )}
                          {canManage && onSetStatus && r.effectiveStatus === "CANCELLED" && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                              onClick={() => onSetStatus(r, "ACTIVE")}
                            >
                              <RotateCcw size={13} /> Reactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Total of what is on screen, so the figure always matches the
                  filter in force rather than the whole register. */}
              <tfoot>
                <tr className="fw-bold">
                  <td colSpan={7} className="text-end">Total</td>
                  <td className="text-end">
                    {money(filtered.reduce((sum, r) => sum + (Number(r.amcAmount) || 0), 0))}
                  </td>
                  {/* Invoice column has nothing to total. Paid and Balance do -
                      "what have we actually collected against what is on screen"
                      is the whole point of showing them here. */}
                  <td />
                  <td className="text-end">
                    {money(filtered.reduce((sum, r) => sum + (Number(r.paidAmount) || 0), 0))}
                  </td>
                  <td className="text-end">
                    {money(
                      filtered.reduce(
                        (sum, r) => sum + ((Number(r.amcAmount) || 0) - (Number(r.paidAmount) || 0)),
                        0
                      )
                    )}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AmcContractList;
